import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Settings2, Save } from "lucide-react";
import { Button } from "../ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * NovaVotingCriteriaEditor — allows admin to edit the 6 committee voting criteria (label + weight).
 * Props:
 *   campaignId
 *   criteria: current array [{key,label,weight,description}]
 *   token
 *   onSaved: callback
 */
export default function NovaVotingCriteriaEditor({ campaignId, criteria, token, onSaved }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(() => JSON.parse(JSON.stringify(criteria || [])));
  const [saving, setSaving] = useState(false);

  const updateRow = (idx, patch) => setRows(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRow = () => setRows([...rows, { key: `crit_${Date.now()}`, label: "Nuevo criterio", weight: 1.0, description: "" }]);
  const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));

  const save = async () => {
    if (!rows.length) { toast.error("Debes tener al menos 1 criterio"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/campaigns/${campaignId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ voting_criteria: rows }),
      });
      if (!r.ok) throw new Error();
      toast.success("Criterios de votación actualizados");
      setOpen(false);
      onSaved && onSaved();
    } catch {
      toast.error("Error guardando criterios");
    } finally { setSaving(false); }
  };

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="edit-voting-criteria" className="gap-1.5 text-xs">
        <Settings2 className="w-3.5 h-3.5"/> Editar criterios de votación
      </Button>
    );
  }

  return (
    <div className="rounded-2xl border border-emerald-300 dark:border-emerald-700 bg-emerald-50/40 dark:bg-emerald-500/5 p-4 space-y-3" data-testid="voting-criteria-editor">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-2"><Settings2 className="w-4 h-4"/> Criterios de votación del Comité</div>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="text-xs">Cerrar</Button>
      </div>
      <div className="text-[11px] text-muted-foreground">Personaliza los 6 criterios con los que el comité votará. El peso se usa para calcular el score global.</div>
      <div className="space-y-2">
        {rows.map((row, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 items-center">
            <input
              value={row.label}
              onChange={(e) => updateRow(idx, { label: e.target.value })}
              placeholder="Nombre del criterio"
              className="col-span-4 px-2 py-1 rounded border border-border bg-background text-xs"
              data-testid={`criterion-label-${idx}`}
            />
            <input
              value={row.description || ""}
              onChange={(e) => updateRow(idx, { description: e.target.value })}
              placeholder="Descripción / ayuda"
              className="col-span-6 px-2 py-1 rounded border border-border bg-background text-xs"
              data-testid={`criterion-desc-${idx}`}
            />
            <input
              type="number" min="0.1" max="5" step="0.1"
              value={row.weight ?? 1}
              onChange={(e) => updateRow(idx, { weight: Number(e.target.value) })}
              className="col-span-1 px-2 py-1 rounded border border-border bg-background text-xs text-center"
              data-testid={`criterion-weight-${idx}`}
              title="Peso"
            />
            <button onClick={() => removeRow(idx)} className="col-span-1 text-muted-foreground hover:text-destructive flex items-center justify-center">
              <Trash2 className="w-3.5 h-3.5"/>
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <Button size="sm" variant="ghost" onClick={addRow} className="text-xs gap-1"><Plus className="w-3 h-3"/> Añadir criterio</Button>
        <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1 text-xs" data-testid="save-voting-criteria">
          {saving ? <Loader2 className="w-3 h-3 animate-spin"/> : <Save className="w-3 h-3"/>}
          Guardar criterios
        </Button>
      </div>
    </div>
  );
}
